import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, Loader2, Send } from "lucide-react";
import { submitCandidate } from "@/lib/api";

const EMPTY = { full_name: "", email: "", phone: "", location: "", experience: "", role: "", preferred_location: "", expertise: "" };

export default function CandidateForm({ job = null }) {
  const [form, setForm] = useState(job ? { ...EMPTY, role: job.title } : EMPTY);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(pdf|doc|docx)$/i.test(f.name)) {
      toast.error("Only PDF, DOC or DOCX resumes are accepted.");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Resume must be under 5MB.");
      return;
    }
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please attach your resume (PDF, DOC or DOCX).");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("job_id", job?.id || "");
      fd.append("job_title", job?.title || "");
      fd.append("resume", file);
      await submitCandidate(fd);
      toast.success("Profile submitted. Our recruitment team will connect you with relevant opportunities.");
      setForm(job ? { ...EMPTY, role: job.title } : EMPTY);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="candidate-resume-form" onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input data-testid="candidate-name-input" required value={form.full_name} onChange={set("full_name")} placeholder="Full Name *" className="input-dark" aria-label="Full Name" />
        <input data-testid="candidate-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className="input-dark" aria-label="Email" />
        <input data-testid="candidate-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="Phone *" className="input-dark" aria-label="Phone" />
        <input data-testid="candidate-location-input" required value={form.location} onChange={set("location")} placeholder="Current Location *" className="input-dark" aria-label="Current Location" />
        <input data-testid="candidate-experience-input" required value={form.experience} onChange={set("experience")} placeholder="Total Experience (e.g. 5 years) *" className="input-dark" aria-label="Total Experience" />
        <input data-testid="candidate-role-input" required value={form.role} onChange={set("role")} placeholder="Current / Most Recent Role *" className="input-dark" aria-label="Current or Most Recent Role" />
        <input data-testid="candidate-preferred-location-input" value={form.preferred_location} onChange={set("preferred_location")} placeholder="Preferred Location" className="input-dark" aria-label="Preferred Location" />
        <input data-testid="candidate-expertise-input" value={form.expertise} onChange={set("expertise")} placeholder="Area of Expertise" className="input-dark" aria-label="Area of Expertise" />
      </div>
      <button
        type="button"
        data-testid="candidate-resume-upload"
        onClick={() => fileRef.current?.click()}
        className="flex w-full items-center justify-center gap-3 rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-4 py-6 text-sm text-slate-400 transition-colors duration-300 hover:border-sky-400/50 hover:text-sky-300"
      >
        <FileUp size={18} className="text-sky-400" />
        {file ? <span className="text-slate-200">{file.name}</span> : "Upload Resume — PDF, DOC or DOCX (max 5MB) *"}
      </button>
      <input ref={fileRef} data-testid="candidate-resume-file-input" type="file" accept=".pdf,.doc,.docx" onChange={onFile} className="hidden" aria-label="Resume file" />
      <button
        data-testid="candidate-form-submit-button"
        type="submit"
        disabled={loading}
        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-sky-500 to-indigo-500 px-7 py-3.5 text-sm font-semibold text-white shadow-[0_8px_30px_rgba(56,189,248,0.25)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_40px_rgba(56,189,248,0.4)] disabled:opacity-60 sm:w-auto"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
        {loading ? "Submitting..." : "Submit Your Resume"}
      </button>
    </form>
  );
}
